import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trainingApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, NotebookPen, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import RegisterDialog from './RegisterDialog';
import RegisterDetail from './RegisterDetail';

const EMPTY_FILTERS = { q: '', store: 'all', tutor: 'all', status: 'all' };

export default function RegistersTab({ meta }) {
  const { user } = useAuth();
  const isOps = user?.role === 'operations_manager';
  const [rows, setRows] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [flt, setFlt] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(() => {
    setSelected(new Set());
    trainingApi.listRegisters()
      .then(r => setRows(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento registri'); setRows([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const totHours = (r) => (r.daily_log || []).reduce((a, e) => a + (parseFloat(e.hours) || 0), 0);

  const uniq = (key) => [...new Set((rows || []).map(r => r[key]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'it'));
  const storeOpts = useMemo(() => uniq('store_name'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const tutorOpts = useMemo(() => uniq('tutor_name'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => (rows || []).filter(r => {
    if (flt.q && !(r.staff_name || '').toLowerCase().includes(flt.q.toLowerCase())) return false;
    if (flt.store !== 'all' && r.store_name !== flt.store) return false;
    if (flt.tutor !== 'all' && r.tutor_name !== flt.tutor) return false;
    if (flt.status !== 'all' && r.status !== flt.status) return false;
    return true;
  }), [rows, flt]);

  const hasFilters = JSON.stringify(flt) !== JSON.stringify(EMPTY_FILTERS);
  const setF = (k, v) => setFlt(f => ({ ...f, [k]: v }));

  const toggleSel = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r.register_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map(r => r.register_id)));

  const bulkDelete = async () => {
    if (!window.confirm(`Eliminare definitivamente ${selected.size} registri selezionati?`)) return;
    setBulkBusy(true);
    try {
      await Promise.all([...selected].map(id => trainingApi.deleteRegister(id)));
      toast.success(`${selected.size} registri eliminati`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eliminazione parzialmente fallita');
    } finally {
      setBulkBusy(false);
      load();
    }
  };

  return (
    <div data-testid="registers-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500" data-testid="reg-filtered-count">
          {rows ? (hasFilters ? `${filtered.length} di ${rows.length} registri` : `${rows.length} registri`) : ''}
        </p>
        {meta.can_manage && (
          <Button onClick={() => setShowNew(true)} className="gap-1.5" data-testid="new-register-btn">
            <Plus className="w-4 h-4" /> Nuovo registro
          </Button>
        )}
      </div>

      {rows !== null && rows.length > 0 && (
        <div className="bg-white border border-slate-200 px-4 py-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="reg-filters-bar">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mr-1">Filtri</span>
          <Input value={flt.q} onChange={e => setF('q', e.target.value)} placeholder="Cerca allievo…"
                 className="h-9 w-full sm:h-8 sm:w-44 rounded-none" data-testid="reg-filter-search" />
          <Select value={flt.store} onValueChange={v => setF('store', v)}>
            <SelectTrigger className="h-9 w-full sm:h-8 sm:w-40 rounded-none" data-testid="reg-filter-store"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i negozi</SelectItem>
              {storeOpts.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={flt.tutor} onValueChange={v => setF('tutor', v)}>
            <SelectTrigger className="h-9 w-full sm:h-8 sm:w-40 rounded-none" data-testid="reg-filter-tutor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tutor</SelectItem>
              {tutorOpts.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={flt.status} onValueChange={v => setF('status', v)}>
            <SelectTrigger className="h-9 w-full sm:h-8 sm:w-32 rounded-none" data-testid="reg-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="in_corso">In corso</SelectItem>
              <SelectItem value="completato">Completato</SelectItem>
            </SelectContent>
          </Select>
          {hasFilters && (
            <button className="text-xs underline text-slate-500 hover:text-slate-900" onClick={() => setFlt(EMPTY_FILTERS)}
                    data-testid="reg-filters-clear">
              Azzera filtri
            </button>
          )}
        </div>
      )}

      {isOps && selected.size > 0 && (
        <div className="bg-slate-900 text-white px-4 py-2.5 mb-4 flex items-center gap-3 flex-wrap" data-testid="reg-bulk-bar">
          <span className="text-sm">{selected.size} selezionati</span>
          <Button variant="destructive" size="sm" className="h-8 gap-1.5 ml-auto" onClick={bulkDelete}
                  disabled={bulkBusy} data-testid="reg-bulk-delete-btn">
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Elimina selezionati
          </Button>
          <button className="text-xs underline text-slate-300" onClick={() => setSelected(new Set())} data-testid="reg-bulk-clear">
            Deseleziona
          </button>
        </div>
      )}

      {rows === null ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400" data-testid="registers-empty">
          <NotebookPen className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>Nessun registro formativo{meta.can_manage ? ' — creane uno con "Nuovo registro"' : ''}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400" data-testid="registers-no-match">
          <p>Nessun registro corrisponde ai filtri</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                {isOps && (
                  <th className="w-8">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="reg-select-all" />
                  </th>
                )}
                <th>Allievo</th><th>Negozio</th><th>Tutor</th><th>Inizio</th>
                <th>Prossimo controllo</th><th>Ore erogate</th><th>Moduli</th><th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.register_id} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setOpenId(r.register_id)} data-testid={`register-row-${r.register_id}`}>
                  {isOps && (
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(r.register_id)}
                             onChange={() => toggleSel(r.register_id)} data-testid={`reg-select-${r.register_id}`} />
                    </td>
                  )}
                  <td data-label="Allievo" className="font-medium text-slate-900">{r.staff_name}</td>
                  <td data-label="Negozio" className="text-sm text-slate-600">{r.store_name || '—'}</td>
                  <td data-label="Tutor" className="text-sm text-slate-600">{r.tutor_name || '—'}</td>
                  <td data-label="Inizio" className="text-sm">{r.start_date || '—'}</td>
                  <td data-label="Controllo" className="text-sm">{r.next_check_date || '—'}</td>
                  <td data-label="Ore" className="text-sm tabular-nums">{totHours(r) || '—'}</td>
                  <td data-label="Moduli" className="text-sm tabular-nums">{(r.modules || []).length}</td>
                  <td data-label="Stato">
                    <Badge variant="outline" className={r.status === 'completato'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'}>
                      {r.status === 'completato' ? 'Completato' : 'In corso'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <RegisterDialog meta={meta} onClose={() => setShowNew(false)}
                        onSaved={(reg) => { setShowNew(false); load(); setOpenId(reg.register_id); }} />
      )}
      {openId && (
        <RegisterDetail registerId={openId} meta={meta}
                        onClose={() => { setOpenId(null); load(); }} />
      )}
    </div>
  );
}
